import React, { useImperativeHandle, useRef, forwardRef } from "react";
import { convertBooleanToOptionArray, formatCurrencyFn, getCheckedBooleanValue, getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import { PaidListLayout, PaidListTotalGroup } from "@icoach/documents/components/PaidList";
import { PaidList } from "@icoach/documents/components";
import { Alert, Box, CheckboxField, TextField } from "@curves/ui-base";
import { useMembershipQuitFormContext } from "@icoach/documents/membership/quit/MembershipQuitFormContext";

const MembershipQuitSettlement101 = forwardRef((_, ref) => {
	const {
		formData: sourceData = {},
		validationErrors,
		updateField,
		isReadOnly,
	} = useMembershipQuitFormContext();

	const {
		contractMonthTerm,
		usedTotalMonth,
		unusedTotalMonth,
		monthlyFee,
		processingFee,
		joinFee,
		refundJoinFee,
		payDifference,
		originalMonthlyFee,
		withSuspendRefund = 0,
		withExtensionRefund = 0,
		withholdingRefund = 0,
		finalRefund,
		isFreeQuitFee,
		totalPrice,
		cashPaymentAmount,
		cardPaymentAmount,
		isRefundCash,
	} = sourceData || {};

	const fieldRef = useRef({});

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return Object.keys(fieldRef.current).reduce((params, key) => {
					let value = fieldRef.current[key]?.getResult();
					if (Array.isArray(value)) value = getCheckedBooleanValue(value); // CheckboxField 會回傳陣列d
					return {
						...params,
						[key]: value,
					};
				}, {});
			},
			isError: () => {
				return refIsRequiredError(fieldRef);
			},
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<PaidListLayout className="mb-3" title="差額費用計算">
			<PaidList.PaidListGroup>
				<PaidList.PaidListItem label="入會費退費">
					<TextField
						ref={el => (fieldRef.current.refundJoinFee = el)}
						key={refundJoinFee}
						defaultValue={refundJoinFee}
						onChangeNative={e => updateField(e, "refundJoinFee")}
						{...getErrorModuleStatus(validationErrors, "refundJoinFee")}
						maskType={"MONEY"}
						disabled
					/>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem>
					<Box className="fee-tip">
						(入會費 <span className="text-info">{formatCurrencyFn(joinFee, { symbol: "$" })}</span> / 合約總月數 <span className="text-info">{contractMonthTerm}</span>) * 未使用月數{" "}
						<span className="text-info">{unusedTotalMonth}</span>
					</Box>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem label="月費補差額">
					<TextField
						ref={el => (fieldRef.current.payDifference = el)}
						key={payDifference}
						defaultValue={payDifference}
						onChangeNative={e => updateField(e, "payDifference")}
						{...getErrorModuleStatus(validationErrors, "payDifference")}
						maskType={"MONEY"}
						disabled
					/>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem>
					<Box className="fee-tip">
						(月費定價 <span className="text-info">{formatCurrencyFn(originalMonthlyFee, { symbol: "$" })}</span> - 月費金額{" "}
						<span className="text-info">{formatCurrencyFn(monthlyFee, { symbol: "$" })}</span>
						{") "}x 實際經過月數 <span className="text-info">{usedTotalMonth}</span>
					</Box>
				</PaidList.PaidListItem>
			</PaidList.PaidListGroup>
			<PaidList.PaidListGroup>
				<PaidList.PaidListItem label="終止費用總計" className={"font-weight-bold"}>
					<strong className="paid-list-cell">{formatCurrencyFn(finalRefund, { symbol: "$" })}</strong>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem label="" className={"mt-0"}>
					<CheckboxField
						ref={el => (fieldRef.current.isFreeQuitFee = el)}
						key={convertBooleanToOptionArray(isFreeQuitFee)}
						defaultValue={convertBooleanToOptionArray(isFreeQuitFee)}
						onChange={e => updateField("isFreeQuitFee", e.target.checked)}
						{...getErrorModuleStatus(validationErrors, "isFreeQuitFee")}
					>
						<CheckboxField.Checkbox label={"免收終止費用"} value={1} />
					</CheckboxField>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem className={"mt-0"}>
					<Box className="fee-tip">
						最多向會員收取 <span className="text-info">{formatCurrencyFn(3600, { symbol: "$" })}</span>
					</Box>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem label="已扣月費退費">
					<TextField defaultValue={(withholdingRefund + withExtensionRefund + withSuspendRefund) * -1} maskType={"MONEY"} disabled />
				</PaidList.PaidListItem>
				<PaidList.PaidListItem>
					<Box className="fee-tip">
						當月已扣月費退回 <span className="text-info">{formatCurrencyFn(withholdingRefund, { symbol: "$" })}</span> + 已扣延展退費{" "}
						<span className="text-info">
							{formatCurrencyFn(withExtensionRefund + withSuspendRefund, {
								symbol: "$",
							})}
						</span>
					</Box>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem label="工本費">
					<TextField
						ref={el => (fieldRef.current.processingFee = el)}
						key={processingFee}
						defaultValue={processingFee}
						onChangeNative={e => updateField("processingFee", e.target.value)}
						{...getErrorModuleStatus(validationErrors, "processingFee")}
						maskType="MONEY"
						readOnly={isReadOnly}
					/>
				</PaidList.PaidListItem>
			</PaidList.PaidListGroup>
			{totalPrice < 1 ? (
				<PaidListTotalGroup total={totalPrice}>
					{totalPrice < 0 && (
						<React.Fragment>
							<Alert className={"mt-2"} severity={"info"} variant={"filled"} color={"error"}>
								此筆退款將合併於批次扣款刷退中一併退還予會員
							</Alert>
							<CheckboxField
								key={isRefundCash}
								ref={el => (fieldRef.current.isRefundCash = el)}
								defaultValue={convertBooleanToOptionArray(isRefundCash)}
								onChange={e => updateField("isRefundCash", e.target.checked)}
								{...getErrorModuleStatus(validationErrors, "isFreeQuitFee")}
								readOnly={isReadOnly}
							>
								<CheckboxField.Checkbox label="現場現金退款：若會員使用既存月費及ACH付款發票會直接折讓，若信用卡扣款則發票折讓但不會刷退" value={1} />
							</CheckboxField>
						</React.Fragment>
					)}
				</PaidListTotalGroup>
			) : (
				<PaidList.PaidListGroup>
					<PaidList.PaidListItem label="合計" className={"font-weight-bold"}>
						<strong className="paid-list-cell text-primary">{formatCurrencyFn(totalPrice)}</strong>
					</PaidList.PaidListItem>
					<PaidList.PaidListItem label="現金收款金額">
						<TextField
							ref={el => (fieldRef.current.cashPaymentAmount = el)}
							key={cashPaymentAmount}
							defaultValue={cashPaymentAmount}
							onChangeNative={e => updateField("cashPaymentAmount", e.target.value)}
							{...getErrorModuleStatus(validationErrors, "cashPaymentAmount")}
							maskType={"MONEY"}
							inputProps={{ min: 0 }}
							readOnly={isReadOnly}
							required
						/>
					</PaidList.PaidListItem>
					<PaidList.PaidListItem label="信用卡收款金額">
						<TextField
							ref={el => (fieldRef.current.cardPaymentAmount = el)}
							key={cardPaymentAmount}
							defaultValue={cardPaymentAmount}
							onChangeNative={e => updateField("cardPaymentAmount", e.target.value)}
							{...getErrorModuleStatus(validationErrors, "cardPaymentAmount")}
							maskType={"MONEY"}
							inputProps={{ min: 0 }}
							readOnly={isReadOnly}
							required
						/>
					</PaidList.PaidListItem>
				</PaidList.PaidListGroup>
			)}
		</PaidListLayout>
	);
});

export default MembershipQuitSettlement101;
